/**
 * Base de données mock en mémoire — formes IDENTIQUES aux réponses GraphQL du
 * backend (fragments AnalysisBase / ProductBase, AnalysisStats, niches…).
 * Consommée par graphqlMockHandlers ; les hooks/mappers/composants de prod ne
 * savent pas qu'ils parlent à un mock. À supprimer au branchement du back v2.
 */

const daysAgoIso = (n: number, hoursOffset = 0): string => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  d.setHours(d.getHours() - hoursOffset);
  return d.toISOString();
};

// ─── Analyses (forme AnalysisBase, enums backend) ──────────────────────────

export interface MockAnalysis {
  id: string;
  name: string;
  pageUrl: string;
  imageUrl: string | null;
  type: 'catalog' | 'profile';
  status: 'starting' | 'active' | 'inactive' | 'error' | 'removed';
  isPublic: boolean;
  userId: string | null;
  publicCategoryId: string | null;
  lastUpdatedAt: string | null;
  inactivatedAt: string | null;
  createdAt: string;
  analysedProductCount: number | null;
  newSells: number | null;
}

export const mockAnalyses: MockAnalysis[] = [
  {
    id: 'mock-1',
    name: 'Sneakers Salomon XT-6',
    pageUrl: 'https://www.vinted.fr/catalog?catalog[]=1242&brand_ids[]=6001',
    imageUrl: 'https://picsum.photos/seed/salomon/72/72',
    type: 'catalog',
    status: 'active',
    isPublic: false,
    userId: 'mock-user',
    publicCategoryId: null,
    lastUpdatedAt: daysAgoIso(0, 2),
    inactivatedAt: null,
    createdAt: daysAgoIso(2),
    analysedProductCount: 486,
    newSells: 34,
  },
  {
    id: 'mock-2',
    name: 'Vestes Carhartt vintage homme',
    pageUrl: 'https://www.vinted.fr/catalog?brand_ids[]=362',
    imageUrl: 'https://picsum.photos/seed/carhartt/72/72',
    type: 'catalog',
    status: 'active',
    isPublic: false,
    userId: 'mock-user',
    publicCategoryId: null,
    lastUpdatedAt: daysAgoIso(0, 5),
    inactivatedAt: null,
    createdAt: daysAgoIso(5),
    analysedProductCount: 1214,
    newSells: 8,
  },
  {
    id: 'mock-3',
    name: 'T-shirts homme Nike · 5-25 € · Très bon état',
    // price_from/price_to + status_ids : dimensions verrouillées dans la barre de segment
    pageUrl: 'https://www.vinted.fr/catalog?catalog[]=77&price_from=5&price_to=25&status_ids[]=2',
    imageUrl: null,
    type: 'catalog',
    status: 'starting',
    isPublic: false,
    userId: 'mock-user',
    publicCategoryId: null,
    lastUpdatedAt: null,
    inactivatedAt: null,
    createdAt: daysAgoIso(0),
    analysedProductCount: null,
    newSells: null,
  },
  {
    id: 'mock-4',
    name: "Jeans Levi's 501 W30-W34 vintage délavé coupe droite années 90",
    // size_ids : dimension taille verrouillée dans la barre de segment
    pageUrl: 'https://www.vinted.fr/catalog?catalog[]=257&brand_ids[]=10&search_text=501&size_ids[]=1226&size_ids[]=1228&size_ids[]=1230',
    imageUrl: 'https://picsum.photos/seed/levis/72/72',
    type: 'catalog',
    status: 'inactive',
    isPublic: false,
    userId: 'mock-user',
    publicCategoryId: null,
    lastUpdatedAt: daysAgoIso(19),
    inactivatedAt: daysAgoIso(19),
    createdAt: daysAgoIso(21),
    analysedProductCount: 1842,
    newSells: null,
  },
  {
    id: 'mock-5',
    name: 'Robes Ralph Lauren femme',
    pageUrl: 'https://www.vinted.fr/catalog?catalog[]=10&brand_ids[]=88',
    imageUrl: 'https://picsum.photos/seed/ralph/72/72',
    type: 'catalog',
    status: 'inactive',
    isPublic: false,
    userId: 'mock-user',
    publicCategoryId: null,
    lastUpdatedAt: daysAgoIso(32),
    inactivatedAt: daysAgoIso(32),
    createdAt: daysAgoIso(34),
    analysedProductCount: 623,
    newSells: null,
  },
  // ─── Analyses VENDEUR (type profile) ─────────────────────────────────────
  {
    id: 'mock-vendor-1',
    name: 'vintage_marketplace',
    pageUrl: 'https://www.vinted.fr/member/10345678-vintage-marketplace',
    imageUrl: 'https://picsum.photos/seed/av1/72/72',
    type: 'profile',
    status: 'active',
    isPublic: false,
    userId: 'mock-user',
    publicCategoryId: null,
    lastUpdatedAt: daysAgoIso(0, 3),
    inactivatedAt: null,
    createdAt: daysAgoIso(4),
    analysedProductCount: 312,
    newSells: 9,
  },
  {
    id: 'mock-vendor-2',
    name: 'sneakers.factory',
    pageUrl: 'https://www.vinted.fr/member/20456789-sneakers-factory',
    imageUrl: 'https://picsum.photos/seed/av2/72/72',
    type: 'profile',
    status: 'active',
    isPublic: false,
    userId: 'mock-user',
    publicCategoryId: null,
    lastUpdatedAt: daysAgoIso(1),
    inactivatedAt: null,
    createdAt: daysAgoIso(12),
    analysedProductCount: 187,
    newSells: null,
  },
  {
    id: 'mock-vendor-3',
    name: 'marie_dressing',
    pageUrl: 'https://www.vinted.fr/member/30567890-marie-dressing',
    imageUrl: 'https://picsum.photos/seed/av3/72/72',
    type: 'profile',
    status: 'inactive',
    isPublic: false,
    userId: 'mock-user',
    publicCategoryId: null,
    lastUpdatedAt: daysAgoIso(25),
    inactivatedAt: daysAgoIso(25),
    createdAt: daysAgoIso(30),
    analysedProductCount: 96,
    newSells: null,
  },
  {
    id: 'mock-6',
    name: 'Maillots de foot rétro',
    pageUrl: 'https://www.vinted.fr/catalog?catalog[]=1806',
    imageUrl: 'https://picsum.photos/seed/foot/72/72',
    type: 'catalog',
    status: 'error',
    isPublic: false,
    userId: 'mock-user',
    publicCategoryId: null,
    lastUpdatedAt: daysAgoIso(41),
    inactivatedAt: null,
    createdAt: daysAgoIso(41),
    analysedProductCount: 112,
    newSells: null,
  },
];

// ─── Stats (forme AnalysisStats) ───────────────────────────────────────────

export const mockAnalysisStats = {
  averageSalePrice: 26.4,
  salePercentage: 19,
  averageSaleTimeSec: 60 * 60 * 24 * 3 + 60 * 60 * 5, // 3j 5h
  totalProductsAnalyzed: 486,
  totalSalesRecorded: 342,
  newProductsPerDay: 41,
  salesPerDay: 12,
  priceDistributionQ1: 18,
  priceDistributionQ3: 34,
  priceDistributionMin: 6,
  priceDistributionMax: 89,
  priceDistributionBuckets: [
    { price: 10, count: 18 },
    { price: 15, count: 42 },
    { price: 20, count: 68 },
    { price: 25, count: 74 },
    { price: 30, count: 55 },
    { price: 35, count: 38 },
    { price: 45, count: 24 },
    { price: 55, count: 13 },
    { price: 70, count: 7 },
    { price: 89, count: 3 },
  ],
};

// ─── Niches (forme AnalysisNicheStats) ─────────────────────────────────────

export const mockNiches = [
  { type: 'brand', niche: 'Salomon', imageUrl: 'https://picsum.photos/seed/n1/56/56', avgPrice: 48.5, productCount: 124, soldPct: 24, soldPerDay: 4.2, addedPerDay: 9, timeToSellHours: 52 },
  { type: 'brand', niche: 'Nike', imageUrl: 'https://picsum.photos/seed/n2/56/56', avgPrice: 22.1, productCount: 312, soldPct: 17, soldPerDay: 7.5, addedPerDay: 31, timeToSellHours: 78 },
  { type: 'brand', niche: 'Carhartt', imageUrl: 'https://picsum.photos/seed/n3/56/56', avgPrice: 39.9, productCount: 98, soldPct: 21, soldPerDay: 2.8, addedPerDay: 6, timeToSellHours: 61 },
  { type: 'brand', niche: 'Ralph Lauren', imageUrl: 'https://picsum.photos/seed/n4/56/56', avgPrice: 31.7, productCount: 156, soldPct: 14, soldPerDay: 3.1, addedPerDay: 12, timeToSellHours: 95 },
  { type: 'brand', niche: 'Adidas', imageUrl: 'https://picsum.photos/seed/n5/56/56', avgPrice: 19.4, productCount: 264, soldPct: 11, soldPerDay: 4.4, addedPerDay: 28, timeToSellHours: 120 },
  { type: 'category', niche: 'Hommes > Chaussures > Baskets', imageUrl: 'https://picsum.photos/seed/n6/56/56', avgPrice: 35.2, productCount: 208, soldPct: 22, soldPerDay: 6.3, addedPerDay: 18, timeToSellHours: 58 },
  { type: 'category', niche: 'Hommes > Vêtements > Vestes', imageUrl: 'https://picsum.photos/seed/n7/56/56', avgPrice: 42.8, productCount: 143, soldPct: 18, soldPerDay: 3.6, addedPerDay: 11, timeToSellHours: 71 },
  { type: 'category', niche: 'Femmes > Robes > Robes mi-longues', imageUrl: 'https://picsum.photos/seed/n8/56/56', avgPrice: 27.3, productCount: 89, soldPct: 9, soldPerDay: 1.2, addedPerDay: 8, timeToSellHours: 168 },
];

// ─── Produits (forme ProductBase) ──────────────────────────────────────────

interface MockProductSeed {
  title: string;
  category: string;
  price: number;
  brand: string;
  itemCondition: string;
  favouriteCount: number;
  size: string;
  color: string;
  soldDaysAgo?: number;
}

const soldSeeds: MockProductSeed[] = [
  { title: 'Salomon XT-6 Advanced noir',
    category: 'Baskets', price: 95, brand: 'Salomon', itemCondition: 'veryGoodCondition', favouriteCount: 23, size: '42', color: 'Noir', soldDaysAgo: 1 },
  { title: 'Salomon XT-6 GTX kaki',
    category: 'Baskets', price: 88, brand: 'Salomon', itemCondition: 'goodCondition', favouriteCount: 17, size: '43', color: 'Vert', soldDaysAgo: 2 },
  { title: 'Veste Carhartt Detroit vintage',
    category: 'Vestes', price: 65, brand: 'Carhartt', itemCondition: 'goodCondition', favouriteCount: 41, size: 'L', color: 'Marron', soldDaysAgo: 2 },
  { title: 'T-shirt Nike vintage center swoosh',
    category: 'T-shirts', price: 18, brand: 'Nike', itemCondition: 'veryGoodCondition', favouriteCount: 9, size: 'M', color: 'Blanc', soldDaysAgo: 3 },
  { title: "Jean Levi's 501 W32 L32 délavé",
    category: 'Jeans', price: 35, brand: "Levi's", itemCondition: 'goodCondition', favouriteCount: 14, size: 'W32', color: 'Bleu', soldDaysAgo: 4 },
  { title: 'Robe Ralph Lauren fleurie',
    category: 'Robes', price: 42, brand: 'Ralph Lauren', itemCondition: 'newWithTag', favouriteCount: 7, size: 'S', color: 'Rouge', soldDaysAgo: 5 },
  { title: 'Salomon Speedcross 5 bleu',
    category: 'Baskets', price: 54, brand: 'Salomon', itemCondition: 'satisfactory', favouriteCount: 5, size: '44', color: 'Bleu', soldDaysAgo: 6 },
  { title: 'Sweat Nike vintage brodé',
    category: 'Sweats', price: 24, brand: 'Nike', itemCondition: 'veryGoodCondition', favouriteCount: 12, size: 'L', color: 'Gris', soldDaysAgo: 8 },
];

const expiredSeeds: MockProductSeed[] = [
  { title: 'Salomon XA Pro 3D usées',
    category: 'Baskets', price: 25, brand: 'Salomon', itemCondition: 'satisfactory', favouriteCount: 1, size: '41', color: 'Noir' },
  { title: 'T-shirt Nike logo délavé',
    category: 'T-shirts', price: 8, brand: 'Nike', itemCondition: 'goodCondition', favouriteCount: 0, size: 'S', color: 'Gris' },
  { title: 'Veste Carhartt tachée',
    category: 'Vestes', price: 30, brand: 'Carhartt', itemCondition: 'satisfactory', favouriteCount: 3, size: 'XL', color: 'Beige' },
];

const removedSeeds: MockProductSeed[] = [
  { title: 'Salomon XT-6 blanche (retirée)',
    category: 'Baskets', price: 110, brand: 'Salomon', itemCondition: 'veryGoodCondition', favouriteCount: 19, size: '42', color: 'Blanc' },
  { title: "Jean Levi's 501 brut (retiré)",
    category: 'Jeans', price: 45, brand: "Levi's", itemCondition: 'goodCondition', favouriteCount: 6, size: 'W34', color: 'Bleu' },
];

// « En vente » : le stock actuellement listé (onglet des analyses vendeur)
const activeSeeds: MockProductSeed[] = [
  { title: 'Salomon XT-6 Mindful kaki',
    category: 'Baskets', price: 92, brand: 'Salomon', itemCondition: 'veryGoodCondition', favouriteCount: 11, size: '43', color: 'Vert' },
  { title: 'Veste Carhartt Michigan navy',
    category: 'Vestes', price: 78, brand: 'Carhartt', itemCondition: 'newWithoutTag', favouriteCount: 27, size: 'M', color: 'Bleu' },
  { title: 'Sweat Nike vintage gris chiné',
    category: 'Sweats', price: 22, brand: 'Nike', itemCondition: 'goodCondition', favouriteCount: 4, size: 'L', color: 'Gris' },
  { title: "Jean Levi's 501 stone wash",
    category: 'Jeans', price: 39, brand: "Levi's", itemCondition: 'veryGoodCondition', favouriteCount: 8, size: 'W32', color: 'Bleu' },
];

const buildProduct = (seed: MockProductSeed, status: 'sold' | 'expired' | 'removed' | 'active', index: number) => {
  const uploadedDaysAgo = (seed.soldDaysAgo ?? 10) + 3 + (index % 5);
  return {
    id: `mock-product-${status}-${index}`,
    url: `https://www.vinted.fr/items/000${index}-mock`,
    title: seed.title,
    description:
      index % 2 === 0
        ? `${seed.title} en ${seed.size}. Très bonne condition, porté quelques fois.\nEnvoi rapide et soigné 📦 N'hésitez pas pour toute question !`
        : null,
    price: seed.price,
    status,
    itemCondition: seed.itemCondition,
    brand: seed.brand,
    favouriteCount: seed.favouriteCount,
    attributes: [
      { key: 'category', name: 'Catégorie', value: seed.category },
      { key: 'size', name: 'Taille', value: seed.size },
      { key: 'color', name: 'Couleur', value: seed.color },
      { key: 'status', name: 'État', value: '' },
    ],
    sellerUrl: 'https://www.vinted.fr/member/000-mock',
    sellerName: `vendeur_${index + 1}`,
    sellerReviews: 40 + index * 17,
    sellerRating: 0.86 + (index % 6) * 0.02,
    uploadedAt: daysAgoIso(uploadedDaysAgo),
    inactivatedAt: status === 'sold' ? daysAgoIso(seed.soldDaysAgo ?? 1) : status === 'active' ? null : daysAgoIso(1),
    lastUpdateAt: daysAgoIso(1),
    createdAt: daysAgoIso(uploadedDaysAgo),
    images: [0, 1, 2].map((n) => ({
      url: `https://picsum.photos/seed/p${status}${index}${n}/320/420`,
      displayOrder: n,
    })),
  };
};

export const mockProductsByStatus: Record<string, ReturnType<typeof buildProduct>[]> = {
  sold: soldSeeds.map((s, i) => buildProduct(s, 'sold', i)),
  expired: expiredSeeds.map((s, i) => buildProduct(s, 'expired', i)),
  removed: removedSeeds.map((s, i) => buildProduct(s, 'removed', i)),
  active: activeSeeds.map((s, i) => buildProduct(s, 'active', i)),
};

// ─── Quotas (forme AnalysisQuota) ──────────────────────────────────────────

export const mockQuotas = {
  hasPartialPublicAnalysisAccess: true,
  hasFullPublicAnalysisAccess: true,
  catalogAnalysesUsed: 2,
  catalogAnalysesMax: 10,
  catalogAnalysesAvailable: 8,
  profileAnalysesUsed: 0,
  profileAnalysesMax: 10,
  profileAnalysesAvailable: 10,
  canCreateCatalogAnalysis: true,
  canCreateProfileAnalysis: true,
};

// ─── Mutations en mémoire ──────────────────────────────────────────────────
// Les analyses créées pendant la démo sont persistées en localStorage : sans
// ça, une recherche lancée (ex. depuis une niche) disparaît au reload alors
// que son organisation (dossier, lien mère→fille) survit, elle.

const CREATED_STORAGE_KEY = 'recop.mockCreatedAnalyses.v1';

const loadCreatedAnalyses = (): MockAnalysis[] => {
  try {
    const raw = window.localStorage.getItem(CREATED_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as MockAnalysis[]) : [];
  } catch {
    return [];
  }
};

// Réinjecte les analyses créées lors des sessions précédentes (en tête,
// comme à leur création).
mockAnalyses.unshift(...loadCreatedAnalyses());

const persistCreatedAnalyses = () => {
  try {
    window.localStorage.setItem(
      CREATED_STORAGE_KEY,
      JSON.stringify(mockAnalyses.filter((a) => a.id.startsWith('mock-created-'))),
    );
  } catch {
    // stockage indisponible : la session en mémoire suffit
  }
};

let createCounter = mockAnalyses.reduce((max, a) => {
  const match = /^mock-created-(\d+)$/.exec(a.id);
  return match ? Math.max(max, Number(match[1])) : max;
}, 0);

export const createMockAnalysis = (input: { title: string; url: string; type: 'catalog' | 'profile' }): MockAnalysis => {
  createCounter += 1;
  const analysis: MockAnalysis = {
    id: `mock-created-${createCounter}`,
    name: input.title,
    pageUrl: input.url,
    imageUrl: `https://picsum.photos/seed/created${createCounter}/72/72`,
    type: input.type,
    status: 'active',
    isPublic: false,
    userId: 'mock-user',
    publicCategoryId: null,
    lastUpdatedAt: daysAgoIso(0),
    inactivatedAt: null,
    createdAt: daysAgoIso(0),
    analysedProductCount: 486,
    newSells: 12,
  };
  mockAnalyses.unshift(analysis);
  persistCreatedAnalyses();
  return analysis;
};

export const findMockAnalysis = (id: string): MockAnalysis | undefined => mockAnalyses.find((a) => a.id === id);

export const updateMockAnalysisName = (id: string, name: string): MockAnalysis | undefined => {
  const analysis = findMockAnalysis(id);
  if (analysis) analysis.name = name;
  persistCreatedAnalyses();
  return analysis;
};

export const deactivateMockAnalysis = (id: string): MockAnalysis | undefined => {
  const analysis = findMockAnalysis(id);
  if (analysis) {
    analysis.status = 'inactive';
    analysis.inactivatedAt = daysAgoIso(0);
  }
  persistCreatedAnalyses();
  return analysis;
};

export const removeMockAnalysis = (id: string): MockAnalysis | undefined => {
  const analysis = findMockAnalysis(id);
  if (analysis) analysis.status = 'removed';
  persistCreatedAnalyses();
  return analysis;
};

// ─── Activité quotidienne (forme cible du futur endpoint snapshots) ────────
// ⚠️ Aucun backend n'existe encore pour cette série temporelle : c'est le
// chantier « table analysis_stats_snapshot » de la roadmap data v2.

export interface MockDailyActivity {
  date: string;
  sales: number;
  added: number;
}

export const mockDailyActivity: MockDailyActivity[] = Array.from({ length: 30 }, (_, i) => {
  const day = 29 - i;
  // Variation pseudo-aléatoire déterministe (pas de Math.random : stabilité du rendu)
  const wave = Math.sin(i * 1.7) * 0.35 + Math.sin(i * 0.6) * 0.25;
  return {
    date: daysAgoIso(day).slice(0, 10),
    sales: Math.max(2, Math.round(12 * (1 + wave))),
    added: Math.max(8, Math.round(41 * (1 + Math.sin(i * 1.1) * 0.3))),
  };
});

// ─── Pattern hebdomadaire ventes/ajouts (forme cible du futur endpoint) ────
// day: 0 = lundi … 6 = dimanche · slot: 0 = 6h-9h … 5 = 21h-24h.
// Calculable dès la v1 depuis soldAt/uploadedAt ; agrégat serveur à créer.

export interface MockWeeklySlot {
  day: number;
  slot: number;
  sales: number;
  added: number;
}

export const mockWeeklyPattern: MockWeeklySlot[] = (() => {
  const result: MockWeeklySlot[] = [];
  for (let day = 0; day < 7; day += 1) {
    for (let slot = 0; slot < 6; slot += 1) {
      // Ventes : pics en soirée (18-24h) et le week-end
      const eveningBoost = slot >= 4 ? 2.2 : slot >= 2 ? 1.2 : 0.6;
      const weekendBoost = day >= 5 ? 1.6 : day === 2 ? 1.15 : 1;
      const salesWave = 1 + Math.sin(day * 2.1 + slot * 1.3) * 0.2;
      // Ajouts : pics le matin (6-9h) et en début de soirée
      const addedBoost = slot === 0 ? 1.8 : slot === 4 ? 1.6 : slot === 1 ? 1.2 : 0.8;
      const addedWave = 1 + Math.sin(day * 1.4 + slot * 0.9) * 0.25;
      result.push({
        day,
        slot,
        sales: Math.round(4 * eveningBoost * weekendBoost * salesWave),
        added: Math.round(9 * addedBoost * addedWave * (day >= 5 ? 1.3 : 1)),
      });
    }
  }
  return result;
})();

// ─── Répartition des temps de vente (forme cible du futur endpoint) ────────
// Histogramme du délai mise en ligne → vente. Calculable dès la v1 depuis
// uploadedAt/soldAt ; agrégat serveur à créer (chantier data v2).
// La somme des counts = totalSalesRecorded (342).

export interface MockSaleTimeBucket {
  /** Borne haute du bucket en heures. */
  maxHours: number;
  count: number;
}

export const mockSaleTimeDistribution: MockSaleTimeBucket[] = [
  { maxHours: 24, count: 64 }, // < 1 j
  { maxHours: 72, count: 96 }, // 1 – 3 j
  { maxHours: 168, count: 88 }, // 3 – 7 j
  { maxHours: 336, count: 48 }, // 7 – 14 j
  { maxHours: 720, count: 30 }, // 14 – 30 j
  { maxHours: 1440, count: 16 }, // 30 – 60 j
];

// ─── Vendeurs de la niche (forme cible du futur endpoint) ──────────────────
// Agrégat par vendeur sur les ventes du périmètre : CA/mois, ventes/mois,
// prix moyen + aperçu des 5 dernières ventes. Calculable dès la v1 depuis
// sellerUrl/soldAt ; agrégat serveur à créer (chantier data v2).

export interface MockSellerLastSale {
  imageUrl: string;
  title: string;
  price: number;
}

export interface MockSellerStat {
  id: string;
  name: string;
  avatarUrl: string | null;
  profileUrl: string;
  rating: number;
  reviews: number;
  monthlyRevenue: number;
  monthlySales: number;
  avgPrice: number;
  lastSales: MockSellerLastSale[];
}

/** Vendeurs avec ≥ 2 ventes sur le périmètre analysé. */
export const mockRecurringSellerCount = 27;

const buildLastSales = (seed: string, basePrice: number): MockSellerLastSale[] =>
  Array.from({ length: 5 }, (_, i) => ({
    imageUrl: `https://picsum.photos/seed/${seed}sale${i}/160/210`,
    title: `Vente ${i + 1}`,
    price: Math.round(basePrice * (0.8 + i * 0.12)),
  }));

export const mockSellers: MockSellerStat[] = [
  {
    id: 'seller-1',
    name: 'vintage_marketplace',
    avatarUrl: 'https://picsum.photos/seed/av1/96/96',
    profileUrl: 'https://www.vinted.fr/member/001-vintage-marketplace',
    rating: 4.9,
    reviews: 1243,
    monthlyRevenue: 2840,
    monthlySales: 31,
    avgPrice: 91.6,
    lastSales: buildLastSales('s1', 88),
  },
  {
    id: 'seller-2',
    name: 'sneakers.factory',
    avatarUrl: 'https://picsum.photos/seed/av2/96/96',
    profileUrl: 'https://www.vinted.fr/member/002-sneakers-factory',
    rating: 4.7,
    reviews: 687,
    monthlyRevenue: 1960,
    monthlySales: 24,
    avgPrice: 81.7,
    lastSales: buildLastSales('s2', 76),
  },
  {
    id: 'seller-3',
    name: 'marie_dressing',
    avatarUrl: 'https://picsum.photos/seed/av3/96/96',
    profileUrl: 'https://www.vinted.fr/member/003-marie-dressing',
    rating: 5,
    reviews: 312,
    monthlyRevenue: 1120,
    monthlySales: 19,
    avgPrice: 58.9,
    lastSales: buildLastSales('s3', 54),
  },
  {
    id: 'seller-4',
    name: 'thrift.hunter',
    avatarUrl: 'https://picsum.photos/seed/av4/96/96',
    profileUrl: 'https://www.vinted.fr/member/004-thrift-hunter',
    rating: 4.6,
    reviews: 954,
    monthlyRevenue: 890,
    monthlySales: 14,
    avgPrice: 63.6,
    lastSales: buildLastSales('s4', 61),
  },
  {
    id: 'seller-5',
    name: 'lucas_resell',
    avatarUrl: 'https://picsum.photos/seed/av5/96/96',
    profileUrl: 'https://www.vinted.fr/member/005-lucas-resell',
    rating: 4.8,
    reviews: 156,
    monthlyRevenue: 640,
    monthlySales: 11,
    avgPrice: 58.2,
    lastSales: buildLastSales('s5', 49),
  },
  {
    id: 'seller-6',
    name: 'closet.cleanout',
    avatarUrl: null,
    profileUrl: 'https://www.vinted.fr/member/006-closet-cleanout',
    rating: 4.3,
    reviews: 87,
    monthlyRevenue: 410,
    monthlySales: 8,
    avgPrice: 51.3,
    lastSales: buildLastSales('s6', 45),
  },
  {
    id: 'seller-7',
    name: 'outdoor_gear_fr',
    avatarUrl: 'https://picsum.photos/seed/av7/96/96',
    profileUrl: 'https://www.vinted.fr/member/007-outdoor-gear',
    rating: 4.9,
    reviews: 445,
    monthlyRevenue: 380,
    monthlySales: 5,
    avgPrice: 76,
    lastSales: buildLastSales('s7', 72),
  },
  {
    id: 'seller-8',
    name: 'seconde.vie',
    avatarUrl: 'https://picsum.photos/seed/av8/96/96',
    profileUrl: 'https://www.vinted.fr/member/008-seconde-vie',
    rating: 4.5,
    reviews: 203,
    monthlyRevenue: 260,
    monthlySales: 6,
    avgPrice: 43.3,
    lastSales: buildLastSales('s8', 40),
  },
];

// ─── Comparaison par caractéristique (forme cible du futur endpoint) ───────
// Uniquement % de vente + nombre de ventes, par état et par taille.

export interface MockCharacteristicStat {
  key: string;
  soldPct: number;
  sales: number;
}

export const mockCharacteristicStats: {
  conditions: MockCharacteristicStat[];
  sizes: MockCharacteristicStat[];
} = {
  conditions: [
    { key: 'newWithTag', soldPct: 27, sales: 48 },
    { key: 'veryGoodCondition', soldPct: 22, sales: 131 },
    { key: 'newWithoutTag', soldPct: 19, sales: 36 },
    { key: 'goodCondition', soldPct: 14, sales: 87 },
    { key: 'satisfactory', soldPct: 7, sales: 29 },
  ],
  sizes: [
    { key: '42', soldPct: 26, sales: 74 },
    { key: '43', soldPct: 23, sales: 66 },
    { key: '41', soldPct: 18, sales: 41 },
    { key: '44', soldPct: 15, sales: 38 },
    { key: '40', soldPct: 11, sales: 22 },
    { key: '45', soldPct: 8, sales: 14 },
  ],
};
